import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { CONSENT_STORAGE_KEY, CONSENT_EVENT } from "@/lib/consent";
import { readFlag, writeFlag } from "@/lib/persistentFlag";

/**
 * CookieConsent · opt-in gate for non-essential (analytics) cookies.
 *
 * Quebec Law 25 (Bill 64) and PIPEDA treat analytics/tracking cookies as
 * non-essential, so they require opt-in consent BEFORE they are set. PostHog
 * (autocapture + analytics cookies) is therefore held dark until the visitor
 * accepts here. See src/lib/posthog.ts for the gate and src/main.tsx for the
 * cr:consent listener that starts PostHog on a late opt-in.
 *
 * Strictly necessary cookies (auth/session) are exempt and always run; this
 * banner does not gate them. The choice persists in localStorage so the banner
 * shows once. "accepted" turns analytics on; "rejected" keeps it off.
 */

type ConsentChoice = "accepted" | "rejected";

function readChoice(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  // localStorage first, cookie fallback: in private browsing setItem throws,
  // and without the cookie the banner came back on every full page load
  // (founder repro 2026-07-31, "one per page").
  const v = readFlag(CONSENT_STORAGE_KEY);
  return v === "accepted" || v === "rejected" ? v : null;
}

export function CookieConsent() {
  const { t } = useTranslation();
  // null = undecided (show banner); a value = decided (hide). Start hidden so
  // the server/prerender markup omits the banner, then reveal on mount if the
  // visitor has not decided yet. Avoids a flash on every page for repeat users.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readChoice() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const decide = (choice: ConsentChoice) => {
    // Writes localStorage AND a strictly-necessary preference cookie, so the
    // choice survives private mode. Analytics still gate on "accepted" only.
    writeFlag(CONSENT_STORAGE_KEY, choice);
    (window as unknown as { __crConsentChoice?: string }).__crConsentChoice = choice;
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: choice }));
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("cookieConsent.aria", "Cookie consent")}
      // z-[95]: above every page-level overlay (demo-tour start screen, mobile
      // tab bar, scanner modals — all ≤90) and below toasts (z-100). At the old
      // z-[60] the banner TIED with page overlays and hit-testing depended on
      // fragile DOM order — the "couldn't tap it away on the demo page" class.
      // print:hidden — page.pdf()/print-to-PDF uses print media, and a FIXED
      // element repeats on EVERY printed page. The demo packet fixtures were
      // once captured with consent undecided and shipped with this banner
      // stamped on all four pages (founder repro 2026-08-07); no capture or
      // customer print should ever include consent chrome.
      className="fixed left-3 right-3 bottom-3 z-[95] print:hidden sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md rounded-xl border border-border bg-card text-card-foreground p-4 sm:p-5 shadow-lg"
    >
      <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
        <Cookie className="h-4 w-4 text-primary" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">
          {t("cookieConsent.title", "Cookies")}
        </span>
      </div>
      <p className="text-[13px] leading-snug sm:text-sm sm:leading-relaxed text-muted-foreground">
        {t(
          "cookieConsent.body",
          "We use strictly necessary cookies to run Cipher & Row. With your permission we also use analytics and advertising cookies (PostHog and the Meta Pixel) to understand which features help and to measure our ads. These stay off until you accept.",
        )}{" "}
        <Link to="/cookies" className="text-primary hover:underline">
          {t("cookieConsent.learnMore", "Cookie Policy")}
        </Link>
        {" · "}
        <Link to="/privacy" className="text-primary hover:underline">
          {t("cookieConsent.privacy", "Privacy")}
        </Link>
      </p>
      <div className="flex items-center gap-2 mt-3 sm:mt-4">
        <button
          type="button"
          onClick={() => decide("rejected")}
          className="px-3 h-9 rounded-md border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
        >
          {t("cookieConsent.reject", "Reject")}
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => decide("accepted")}
          className="px-4 h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          {t("cookieConsent.accept", "Accept")}
        </button>
      </div>
    </div>
  );
}

export default CookieConsent;
